import { useEffect, useMemo, useState } from 'react'
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startRegistration,
} from '@simplewebauthn/browser'
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import ErrorBoundary from '@/components/ErrorBoundary'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import type { LucideIcon } from 'lucide-react'
import {
  LayoutDashboard,
  Users,
  User,
  UserRound,
  UserCog,
  BookOpen,
  BookMarked,
  CalendarRange,
  ClipboardList,
  Award,
  CalendarCheck,
  DoorOpen,
  Target,
  Bell,
  Megaphone,
  ListChecks,
  BellRing,
  LogOut,
  Menu,
  PanelLeft,
  PanelLeftClose,
  X,
  Fingerprint,
  GraduationCap,
  Compass,
  HeartHandshake,
  Library,
  Ruler,
  ScrollText,
  Bug,
  Truck,
  ChevronDown,
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { authService } from '@/modules/auth/services/authService'
import {
  ROUTES,
  gradesActasPath,
  isGradesActasLocation,
} from '@/shared/constants/routes'
import { ROLE_LABELS, UserRole } from '@/shared/constants/roles'
import { messageService } from '@/modules/notifications/services/messageService'
import { settingService } from '@/modules/system/services/settingService'
import { mergeEnabledModules, moduleAccessPermission, pathnameToModuleId, type SystemModuleId } from '@/shared/constants/systemModules'
import { hasPermissionOrAllowLegacy } from '@/shared/utils/permissions'

type MenuChild = {
  path: string
  label: string
  icon: LucideIcon
  moduleId: SystemModuleId
}

type MenuItem = {
  path: string
  label: string
  icon: LucideIcon
  moduleId: SystemModuleId | null
  children?: MenuChild[]
}

const USERS_MENU_KEY = '__menu_usuarios__'
const SIDEBAR_STORAGE_KEY = 'kairos.sidebarOpen'

function readSidebarOpenPreference(): boolean {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
    if (stored === 'false') return false
    if (stored === 'true') return true
  } catch {
    /* localStorage no disponible */
  }
  return true
}

function gradesMenuItem(canCreateGrades: boolean): MenuItem {
  const item: MenuItem = {
    path: ROUTES.GRADES,
    label: 'Calificaciones',
    icon: Award,
    moduleId: 'grades',
  }
  if (canCreateGrades) {
    item.children = [
      { path: ROUTES.GRADES, label: 'Notas Cursada', icon: ClipboardList, moduleId: 'grades' },
      { path: gradesActasPath(), label: 'Notas Actas', icon: ScrollText, moduleId: 'grades' },
    ]
  }
  return item
}

function usersMenuItem(): MenuItem {
  return {
    path: USERS_MENU_KEY,
    label: 'Adm. Usuarios',
    icon: Users,
    moduleId: null,
    children: [
      { path: ROUTES.USERS, label: 'Cuentas y roles', icon: UserCog, moduleId: 'users' },
      { path: ROUTES.STUDENTS, label: 'Estudiantes', icon: Users, moduleId: 'students' },
      { path: ROUTES.TEACHERS, label: 'Docentes', icon: User, moduleId: 'teachers' },
      { path: ROUTES.TUTORS, label: 'Tutores', icon: UserRound, moduleId: 'tutors' },
    ],
  }
}

function childPathname(childPath: string): string {
  return childPath.split('?')[0] ?? childPath
}

function isMenuChildActive(childPath: string, pathname: string, search: string): boolean {
  if (pathname === ROUTES.GRADES) {
    const [, query = ''] = childPath.split('?')
    const childActas = new URLSearchParams(query).get('seccion') === 'actas'
    return childActas === isGradesActasLocation(pathname, search)
  }
  return pathname === childPathname(childPath)
}

function isMenuGroupActive(item: MenuItem, pathname: string, search: string): boolean {
  if (item.children?.length) {
    return item.children.some((child) => isMenuChildActive(child.path, pathname, search))
  }
  return pathname === item.path
}

function isChildModuleVisible(
  moduleId: SystemModuleId,
  enabled: Record<SystemModuleId, boolean>,
  permissions: string[] | undefined,
  role: string | null
): boolean {
  return (
    enabled[moduleId] !== false &&
    hasPermissionOrAllowLegacy(permissions, moduleAccessPermission(moduleId), role)
  )
}

function filterMenuItemChildren(
  item: MenuItem,
  enabled: Record<SystemModuleId, boolean>,
  permissions: string[] | undefined,
  role: string | null
): MenuItem | null {
  if (!item.children?.length) return item
  const children = item.children.filter((child) =>
    isChildModuleVisible(child.moduleId, enabled, permissions, role)
  )
  if (children.length === 0) return null
  return { ...item, children }
}

const baseMenuItems: MenuItem[] = [
  { path: ROUTES.DASHBOARD, label: 'Dashboard', icon: LayoutDashboard, moduleId: null },
  { path: ROUTES.STUDY_PLANS, label: 'Planes de estudios', icon: Library, moduleId: 'studyPlans' },
  { path: ROUTES.GRADE_SCALES, label: 'Escalas de notas', icon: Ruler, moduleId: 'gradeScales' },
  { path: ROUTES.ACTA_BOOKS, label: 'Libro de actas', icon: ScrollText, moduleId: 'actaBooks' },
  { path: ROUTES.EL_BICHO, label: 'El bicho', icon: Bug, moduleId: 'elBicho' },
  { path: ROUTES.COURSES, label: 'Cursos', icon: BookOpen, moduleId: 'courses' },
  { path: ROUTES.PERIODS, label: 'Períodos', icon: CalendarRange, moduleId: 'periods' },
  { path: ROUTES.ENROLLMENTS, label: 'Matrículas', icon: ClipboardList, moduleId: 'enrollments' },
  { path: ROUTES.ATTENDANCE, label: 'Asistencia', icon: CalendarCheck, moduleId: 'attendance' },
  { path: ROUTES.CLASSROOMS, label: 'Aulas', icon: DoorOpen, moduleId: 'classrooms' },
  { path: ROUTES.LOGISTICS, label: 'Logística', icon: Truck, moduleId: 'logistics' },
  { path: ROUTES.FOLLOW_UP, label: 'Seguimiento', icon: Target, moduleId: 'followUp' },
]

export default function MainLayout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [desktopOpen, setDesktopOpen] = useState(readSidebarOpenPreference)
  const [passkeyInMenu, setPasskeyInMenu] = useState(false)
  const [linkingPasskey, setLinkingPasskey] = useState(false)
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({})
  const navigate = useNavigate()
  const location = useLocation()
  const { getFullName, logout, role, userId, permissions } = useAuthStore()

  const canCreateGrades = hasPermissionOrAllowLegacy(permissions, 'grades.create', role)

  const menuItems: MenuItem[] = useMemo(() => {
    if (role === UserRole.TEACHER) {
      return [
        {
          path: ROUTES.TEACHER_PORTAL,
          label: 'Mi espacio docente',
          icon: GraduationCap,
          moduleId: 'courses',
        },
      ]
    }
    if (role === UserRole.STUDENT) {
      return [
        {
          path: ROUTES.STUDENT_PORTAL,
          label: 'Mi espacio estudiante',
          icon: GraduationCap,
          moduleId: 'grades',
        },
        {
          path: ROUTES.STUDENT_ATTENDANCE,
          label: 'Mi asistencia',
          icon: CalendarCheck,
          moduleId: 'attendance',
        },
        {
          path: ROUTES.STUDENT_ANNOUNCEMENTS,
          label: 'Comunicados',
          icon: Megaphone,
          moduleId: 'notifications',
        },
        {
          path: ROUTES.NOTIFICATIONS,
          label: 'Notificaciones',
          icon: Bell,
          moduleId: 'notifications',
        },
        {
          path: ROUTES.STUDENT_LEARNING_PLAN,
          label: 'Plan IA (aprendizaje)',
          icon: Compass,
          moduleId: 'grades',
        },
      ]
    }
    if (role === UserRole.TUTOR) {
      return [
        {
          path: ROUTES.TUTOR_PORTAL,
          label: 'Mi espacio de tutoría',
          icon: UserRound,
          moduleId: 'students',
        },
        {
          path: ROUTES.TUTOR_STUDENT_PLAN,
          label: 'Planes de tutoría',
          icon: BookMarked,
          moduleId: 'students',
        },
        {
          path: ROUTES.STUDENTS,
          label: 'Expedientes',
          icon: Users,
          moduleId: 'students',
        },
        gradesMenuItem(canCreateGrades),
        {
          path: ROUTES.ATTENDANCE,
          label: 'Asistencia',
          icon: CalendarCheck,
          moduleId: 'attendance',
        },
        {
          path: ROUTES.FOLLOW_UP,
          label: 'Seguimiento',
          icon: Target,
          moduleId: 'followUp',
        },
        {
          path: ROUTES.NOTIFICATIONS,
          label: 'Notificaciones',
          icon: Bell,
          moduleId: 'notifications',
        },
      ]
    }
    if (role === UserRole.PARENT) {
      return [
        {
          path: ROUTES.PARENT_PORTAL,
          label: 'Mi espacio familiar',
          icon: HeartHandshake,
          moduleId: 'grades',
        },
        gradesMenuItem(canCreateGrades),
        {
          path: ROUTES.PARENT_ATTENDANCE,
          label: 'Asistencia',
          icon: CalendarCheck,
          moduleId: 'attendance',
        },
        {
          path: ROUTES.PARENT_TUTOR_PLAN,
          label: 'Plan de tutoría',
          icon: BookMarked,
          moduleId: 'grades',
        },
        {
          path: ROUTES.NOTIFICATIONS,
          label: 'Notificaciones',
          icon: Bell,
          moduleId: 'notifications',
        },
      ]
    }
    return [
      { path: ROUTES.DASHBOARD, label: 'Dashboard', icon: LayoutDashboard, moduleId: null },
      { path: ROUTES.INITIAL_SETUP, label: 'Configuración inicial', icon: ListChecks, moduleId: 'initialSetup' },
      { path: ROUTES.NOTIFICATIONS, label: 'Notificaciones', icon: Bell, moduleId: 'notifications' },
      { path: ROUTES.ANNOUNCEMENTS, label: 'Comunicados', icon: Megaphone, moduleId: 'notifications' },
      usersMenuItem(),
      ...baseMenuItems.slice(1, 5),
      gradesMenuItem(canCreateGrades),
      ...baseMenuItems.slice(5),
    ]
  }, [role, canCreateGrades])

  const { data: enabledModulesRaw } = useQuery({
    queryKey: ['settings', 'enabled-modules'],
    queryFn: settingService.getEnabledModules,
    staleTime: 60_000,
  })
  const enabled = useMemo(() => mergeEnabledModules(enabledModulesRaw), [enabledModulesRaw])

  const visibleMenuItems = useMemo(
    () =>
      menuItems
        .map((item) => filterMenuItemChildren(item, enabled, permissions, role))
        .filter(
          (item): item is MenuItem =>
            item != null &&
            (item.children?.length != null ||
              item.moduleId === null ||
              (enabled[item.moduleId] !== false &&
                hasPermissionOrAllowLegacy(permissions, moduleAccessPermission(item.moduleId), role)))
        ),
    [menuItems, enabled, permissions, role]
  )

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(desktopOpen))
    } catch {
      /* localStorage no disponible */
    }
  }, [desktopOpen])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!browserSupportsWebAuthn()) return
      try {
        if (await platformAuthenticatorIsAvailable() && !cancelled) {
          setPasskeyInMenu(true)
        }
      } catch {
        /* sin autenticador de plataforma */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const fallbackRoute =
    role === UserRole.TEACHER
      ? ROUTES.TEACHER_PORTAL
      : role === UserRole.STUDENT
        ? ROUTES.STUDENT_PORTAL
        : role === UserRole.PARENT
          ? ROUTES.PARENT_PORTAL
          : role === UserRole.TUTOR
            ? ROUTES.TUTOR_PORTAL
            : ROUTES.DASHBOARD

  useEffect(() => {
    if (role === UserRole.TEACHER && location.pathname === ROUTES.DASHBOARD) {
      navigate(ROUTES.TEACHER_PORTAL, { replace: true })
      return
    }
    if (role === UserRole.STUDENT && location.pathname === ROUTES.DASHBOARD) {
      navigate(ROUTES.STUDENT_PORTAL, { replace: true })
      return
    }
    if (role === UserRole.PARENT && location.pathname === ROUTES.DASHBOARD) {
      navigate(ROUTES.PARENT_PORTAL, { replace: true })
      return
    }
    if (role === UserRole.TUTOR && location.pathname === ROUTES.DASHBOARD) {
      navigate(ROUTES.TUTOR_PORTAL, { replace: true })
      return
    }
    const mod = pathnameToModuleId(location.pathname)
    if (mod && enabled[mod] === false) {
      navigate(fallbackRoute, { replace: true })
      toast.info('Esta sección está deshabilitada por el administrador.')
      return
    }
    if (mod && !hasPermissionOrAllowLegacy(permissions, moduleAccessPermission(mod), role)) {
      navigate(fallbackRoute, { replace: true })
      toast.info('Tu rol no tiene permiso para acceder a este módulo.')
    }
  }, [location.pathname, enabled, navigate, permissions, role, fallbackRoute])

  useEffect(() => {
    const next: Record<string, boolean> = {}
    if (location.pathname === ROUTES.GRADES) {
      next[ROUTES.GRADES] = true
    }
    if (
      location.pathname === ROUTES.USERS ||
      location.pathname === ROUTES.STUDENTS ||
      location.pathname === ROUTES.TEACHERS ||
      location.pathname === ROUTES.TUTORS
    ) {
      next[USERS_MENU_KEY] = true
    }
    if (Object.keys(next).length > 0) {
      setOpenSubmenus((prev) => ({ ...prev, ...next }))
    }
  }, [location.pathname])

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['messages-unread-count', userId],
    queryFn: () => messageService.getUnreadCount(userId!),
    enabled: !!userId,
    refetchInterval: 30_000, // Polling cada 30 s para actualizar el badge de no leídos
  })
  const hasUnread = unreadCount > 0

  const handleLogout = () => {
    // Revoca el refresh token en el backend (best-effort); no bloquea el cierre de sesión local.
    const refreshToken = localStorage.getItem('refreshToken')
    if (refreshToken) {
      void authService.logout(refreshToken).catch(() => {})
    }
    logout()
    navigate(ROUTES.LOGIN, { replace: true })
  }

  const handleLinkPasskey = async () => {
    if (linkingPasskey) return
    setLinkingPasskey(true)
    try {
      const begin = await authService.webauthnRegisterBegin()
      const optionsJSON = JSON.parse(begin.optionsJSON) as PublicKeyCredentialCreationOptionsJSON
      const credential = await startRegistration({ optionsJSON })
      await authService.webauthnRegisterFinish({
        requestId: begin.requestId,
        credential: credential as unknown as Record<string, unknown>,
      })
      toast.success('Dispositivo vinculado. Ya podés usar huella o Face ID para entrar.')
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : e instanceof Error
            ? e.message
            : 'No se pudo vincular el dispositivo'
      toast.error(msg || 'No se pudo vincular el dispositivo')
    } finally {
      setLinkingPasskey(false)
    }
  }

  const roleLabel = role ? (ROLE_LABELS[role as keyof typeof ROLE_LABELS] ?? role) : '—'

  const NavContent = () => (
    <div className="pt-4 pb-4">
      <p className="px-5 mb-1 text-sm font-medium text-slate-800 truncate" title={getFullName()}>
        {getFullName()}
      </p>
      <p className="px-5 mb-4 text-xs text-slate-500">
        Rol: {roleLabel}
      </p>
      <nav className="flex flex-col">
        {visibleMenuItems.map((item) => {
          const { path, label, icon: Icon, children } = item
          if (children?.length) {
            const groupActive = isMenuGroupActive(item, location.pathname, location.search)
            const isOpen = openSubmenus[path] ?? groupActive
            return (
              <div key={path}>
                <button
                  type="button"
                  onClick={() =>
                    setOpenSubmenus((prev) => ({ ...prev, [path]: !isOpen }))
                  }
                  className={`flex items-center gap-3 px-5 py-2.5 text-left w-full transition-colors ${
                    groupActive
                      ? 'bg-blue-50/60 text-blue-700 font-medium'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span className="flex-1">{label}</span>
                  <ChevronDown
                    className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isOpen &&
                  children.map((child) => {
                    const ChildIcon = child.icon
                    const childActive = isMenuChildActive(
                      child.path,
                      location.pathname,
                      location.search
                    )
                    return (
                      <button
                        key={child.path}
                        type="button"
                        onClick={() => {
                          navigate(child.path)
                          setMobileOpen(false)
                        }}
                        className={`flex items-center gap-3 pl-11 pr-5 py-2 text-left w-full text-sm transition-colors ${
                          childActive
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                      >
                        <ChildIcon className="w-4 h-4 shrink-0" />
                        <span>{child.label}</span>
                      </button>
                    )
                  })}
              </div>
            )
          }
          return (
            <button
              key={path}
              type="button"
              onClick={() => {
                navigate(path)
                setMobileOpen(false)
              }}
              className={`flex items-center gap-3 px-5 py-2.5 text-left w-full transition-colors ${
                location.pathname === path
                  ? 'bg-blue-50 text-blue-700 font-medium'
                  : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" />
              <span>{label}</span>
            </button>
          )
        })}
        {passkeyInMenu && role !== UserRole.TEACHER && role !== UserRole.TUTOR && (
          <button
            type="button"
            disabled={linkingPasskey}
            onClick={handleLinkPasskey}
            className="flex items-center gap-3 px-5 py-2.5 mt-2 text-left w-full text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-60"
          >
            <Fingerprint className="w-5 h-5 shrink-0" />
            <span>{linkingPasskey ? 'Vinculando…' : 'Vincular huella / Face ID'}</span>
          </button>
        )}
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-3 px-5 py-2.5 mt-4 text-left w-full text-red-600 hover:bg-red-50 transition-colors"
        >
          <LogOut className="w-5 h-5 shrink-0" />
          <span>Cerrar sesión</span>
        </button>
      </nav>
    </div>
  )

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* App bar */}
      <header className={`fixed top-0 left-0 right-0 z-30 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 transition-[padding] ${desktopOpen ? 'md:pl-[260px]' : 'md:pl-4'}`}>
        <div className="flex items-center min-w-0">
          <button
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 -ml-2 rounded-lg text-slate-600 hover:bg-slate-100"
            aria-label={mobileOpen ? 'Ocultar menú' : 'Mostrar menú'}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <button
            type="button"
            onClick={() => setDesktopOpen(!desktopOpen)}
            className="hidden md:inline-flex p-2 -ml-2 rounded-lg text-slate-600 hover:bg-slate-100"
            aria-label={desktopOpen ? 'Ocultar menú' : 'Mostrar menú'}
          >
            {desktopOpen ? (
              <PanelLeftClose className="w-6 h-6" />
            ) : (
              <PanelLeft className="w-6 h-6" />
            )}
          </button>
          <img
            src="/kairos.svg"
            alt="Kairos"
            className="ml-2 h-8 w-auto max-w-[160px] object-contain sm:h-9 sm:max-w-[200px] md:h-10 md:max-w-[240px]"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            if (enabled.notifications === false) {
              toast.info('Las notificaciones están deshabilitadas en la configuración del sistema.')
              return
            }
            if (!hasPermissionOrAllowLegacy(permissions, moduleAccessPermission('notifications'), role)) {
              toast.info('Tu rol no tiene permiso para ver notificaciones.')
              return
            }
            navigate(ROUTES.NOTIFICATIONS)
          }}
          className="relative p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-800 ml-2"
          aria-label={hasUnread ? `${unreadCount} notificaciones sin leer` : 'Notificaciones'}
        >
          {hasUnread ? (
            <BellRing className="w-6 h-6 text-amber-600" aria-hidden />
          ) : (
            <Bell className="w-6 h-6" aria-hidden />
          )}
          {hasUnread && (
            <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}
      <aside
        className={`fixed left-0 z-20 w-[260px] bg-white border-r border-slate-200 overflow-y-auto transition-transform top-0 h-full md:top-14 md:h-[calc(100vh-3.5rem)] ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${desktopOpen ? 'md:translate-x-0' : 'md:-translate-x-full'}`}
      >
        <div className="hidden md:flex items-center justify-end border-b border-slate-100 px-3 py-2">
          <button
            type="button"
            onClick={() => setDesktopOpen(false)}
            className="inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            aria-label="Ocultar menú"
            title="Ocultar menú"
          >
            <PanelLeftClose className="w-4 h-4 shrink-0" />
            <span>Ocultar</span>
          </button>
        </div>
        <NavContent />
      </aside>

      {/* Main content */}
      <main
        className={`flex-1 min-w-0 w-full max-w-full overflow-x-hidden min-h-screen pt-14 px-3 pb-3 sm:px-4 sm:pb-4 md:px-6 md:pb-6 transition-[padding] ${desktopOpen ? 'md:pl-[260px]' : 'md:pl-0'}`}
        style={{ minHeight: 'calc(100vh - 3.5rem)' }}
      >
        {/* Aísla los errores de cada página: un fallo no tira el layout y se resetea al navegar. */}
        <ErrorBoundary variant="page" resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  )
}
